import {
  Children,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { MiButton, MiChip } from "@miplatform/components";
import { ButtonVariants, Colors } from "@miplatform/types";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import { Box, SxProps, Tooltip, Typography, useTheme } from "@mui/material";

interface HorizontalScrollContainerProps {
  headerArrows?: boolean;
  children?: ReactNode;
  disableScroll?: boolean;
  scrollX?: number;
  sx?: SxProps;
  headerSx?: SxProps;
  reset?: unknown;
  scrollContainerSx?: SxProps;
  headerPerPage?: number;
  headers?: {
    title: string;
    count?: number;
  }[];
  renderHeader?: () => ReactNode;
}

enum ScrollDirection {
  Left = "left",
  Right = "right",
}

const DEFAULT_SCROLL_AMOUNT = 200;

export const HorizontalScrollContainer = ({
  headerArrows = false,
  children,
  disableScroll = false,
  scrollX,
  sx,
  headerSx,
  reset,
  headers,
  headerPerPage = 1,
  scrollContainerSx,
  renderHeader,
}: HorizontalScrollContainerProps) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const headerContainerRef = useRef<HTMLDivElement | null>(null);
  const leftArrowButtonRef = useRef<HTMLButtonElement | null>(null);
  const rightArrowButtonRef = useRef<HTMLButtonElement | null>(null);
  const [headerWidth, setHeaderWidth] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const getStepWidth = useCallback((): number => {
    if (scrollX) return scrollX;

    if (!containerRef.current) return DEFAULT_SCROLL_AMOUNT;

    const childrenArray = Children.toArray(children);
    const count = childrenArray.length;

    if (count === 0) return DEFAULT_SCROLL_AMOUNT;

    const itemWidthWithGaps = containerRef.current.scrollWidth / count;

    return itemWidthWithGaps;
  }, [scrollX, children]);

  const canScroll = useCallback(() => {
    if (!containerRef.current) return;
    setCanScrollLeft(containerRef.current.scrollLeft > 0);
    setCanScrollRight(
      containerRef.current.scrollWidth >
        containerRef.current.clientWidth + containerRef.current.scrollLeft
    );
  }, []);

  const handleScroll = useCallback(
    (direction: ScrollDirection) => {
      if (!containerRef.current) return;

      const stepWidth = getStepWidth();
      const currentScrollLeft = containerRef.current.scrollLeft;
      const targetIndex =
        Math.round(currentScrollLeft / stepWidth) +
        (direction === ScrollDirection.Right ? 1 : -1);
      const targetScrollLeft = Math.max(0, targetIndex * stepWidth);

      containerRef.current.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth",
      });
      headerContainerRef.current?.scrollTo({
        left:
          (direction === "right"
            ? headerWidth * targetIndex
            : Math.max(0, headerWidth * targetIndex)) * (headerPerPage || 1),
        behavior: "smooth",
      });
    },
    [headerWidth, headerPerPage, getStepWidth]
  );

  const handleScrollLeft = useCallback(
    () => handleScroll(ScrollDirection.Left),
    [handleScroll]
  );

  const handleScrollRight = useCallback(
    () => handleScroll(ScrollDirection.Right),
    [handleScroll]
  );

  useEffect(() => {
    containerRef.current?.scrollTo({ left: 0 });
    headerContainerRef.current?.scrollTo({ left: 0 });
  }, [reset]);

  useLayoutEffect(() => {
    canScroll();
  }, [children, canScroll]);

  useLayoutEffect(() => {
    if (!containerRef.current) return () => {};

    const observer = new ResizeObserver(canScroll);

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (headerArrows && headers) {
      setHeaderWidth(
        (getStepWidth() -
          ((leftArrowButtonRef.current?.offsetWidth || 0) +
            (rightArrowButtonRef.current?.offsetWidth || 0)) -
          33) /
          headerPerPage
      );
    }
  }, [headerPerPage, headerArrows, headers]);

  return (
    <Box
      sx={{
        display: "flex",
        overflow: "hidden",
        ...(headerArrows && {
          flexDirection: "column",
        }),
        ...sx,
      }}
    >
      {headerArrows && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2.5}
          sx={{
            backgroundColor: theme.palette.grey.light,
            padding: theme.spacing(1),
            border: `1px solid ${theme.palette.grey.accent}`,
            ...headerSx,
          }}
        >
          <MiButton
            ref={leftArrowButtonRef}
            variant={ButtonVariants.Outlined}
            size="small"
            data-testid="left-icon"
            disabled={!canScrollLeft}
            sx={{
              borderRadius: 0,
              width: 30,
              height: 30,
              maxWidth: 30,
              maxHeight: 30,
              padding: 0,
              backgroundColor: theme.palette.grey.light,
              border: `1px solid ${theme.palette.grey.lightAccent}`,
            }}
            color={Colors.White}
            startIcon={{
              path: mdiChevronLeft,
              iconColor: canScrollLeft
                ? theme.palette.grey.medium
                : theme.palette.grey.lightAccent,
              iconSize: 1,
            }}
            onClick={handleScrollLeft}
          />
          {headers && (
            <Box
              ref={headerContainerRef}
              sx={{
                display: "flex",
                flex: 1,
                width: "100%",
                overflow: "hidden",
                alignItems: "center",
              }}
            >
              {headers.map((header, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    maxWidth: headerWidth,
                    minWidth: headerWidth,
                  }}
                >
                  <Tooltip title={header.title}>
                    <Typography
                      fontSize={theme.fontSize.medium}
                      fontWeight={theme.fontWeight.semiBold}
                      maxWidth={headerWidth - 65}
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {header.title}
                    </Typography>
                  </Tooltip>
                  <MiChip
                    label={header.count ?? 0}
                    size="small"
                    color="error"
                    sx={{ height: 14, fontSize: 11 }}
                  />
                  <Box
                    sx={{
                      backgroundColor: theme.palette.grey.accent,
                      padding: theme.spacing(0, 1),
                      height: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `1px solid ${theme.palette.grey.main}`,
                    }}
                  >
                    <Typography fontSize={theme.fontSize.xxsmall}>
                      {index + 1}/{headers.length}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {renderHeader?.()}
          <MiButton
            ref={rightArrowButtonRef}
            variant={ButtonVariants.Outlined}
            size="small"
            data-testid="right-icon"
            disabled={!canScrollRight}
            sx={{
              borderRadius: 0,
              width: 30,
              height: 30,
              maxWidth: 30,
              maxHeight: 30,
              padding: 0,
              backgroundColor: theme.palette.grey.light,
              border: `1px solid ${theme.palette.grey.lightAccent}`,
            }}
            color={Colors.White}
            startIcon={{
              path: mdiChevronRight,
              iconColor: canScrollRight
                ? theme.palette.grey.medium
                : theme.palette.grey.lightAccent,
              iconSize: 1,
            }}
            onClick={handleScrollRight}
          />
        </Box>
      )}
      {!headerArrows && (
        <MiButton
          variant={ButtonVariants.Outlined}
          size="small"
          data-testid="left-icon"
          disabled={!canScrollLeft}
          sx={{
            height: "100%",
            borderRadius: 0,
            minWidth: 29,
            maxWidth: 29,
            backgroundColor: theme.palette.grey.light,
            border: `1px solid ${theme.palette.grey.lightAccent}`,
          }}
          color={Colors.White}
          startIcon={{
            path: mdiChevronLeft,
            iconColor: canScrollLeft
              ? theme.palette.grey.medium
              : theme.palette.grey.lightAccent,
            iconSize: 1,
          }}
          onClick={handleScrollLeft}
        />
      )}
      <Box
        ref={containerRef}
        onScroll={canScroll}
        sx={{
          display: "flex",
          overflowX: disableScroll ? "hidden" : "auto",
          overflowY: "hidden",
          scrollBehavior: "smooth",
          whiteSpace: "nowrap",
          width: "100%",
          minWidth: 0,
          ...scrollContainerSx,
        }}
      >
        {children}
      </Box>
      {!headerArrows && (
        <MiButton
          variant={ButtonVariants.Outlined}
          size="small"
          data-testid="right-icon"
          disabled={!canScrollRight}
          sx={{
            height: "100%",
            borderRadius: 0,
            minWidth: 29,
            maxWidth: 29,
            backgroundColor: theme.palette.grey.light,
            border: `1px solid ${theme.palette.grey.lightAccent}`,
          }}
          color={Colors.White}
          startIcon={{
            path: mdiChevronRight,
            iconColor: canScrollRight
              ? theme.palette.grey.medium
              : theme.palette.grey.lightAccent,
            iconSize: 1,
          }}
          onClick={handleScrollRight}
        />
      )}
    </Box>
  );
};
